import React, { useEffect, useState } from 'react';
import { useContracts } from '../../hooks/useContract';
import { formatUSDT, shortenAddress } from '../../utils/format';
import { Spinner } from '../common/Loader';

const POOL_NAMES = ['X1', 'X2', 'X3'];
const POOL_LABELS = ['X1 Pool', 'X2 Pool', 'X3 Pool'];
const POOL_COLORS = ['#5CB0FF', '#C79BFF', '#FFC66B'];
const PAGE_SIZE = 20;

/**
 * Prominent "Eligible Members" browser at the top of the Pools tab.
 * A user in a pool = qualifies for that pool (contract auto-moves them via
 * refreshEligibility on any tx). Uses getPoolMembersPaginated — cheap.
 */
export default function PoolEligibleMembers({ counts }) {
  const { read } = useContracts();
  const [poolTab, setPoolTab] = useState(0); // 0=X1, 1=X2, 2=X3
  const [page, setPage] = useState(0);
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async (pool, pg) => {
    if (!read.pools) return;
    setLoading(true);
    try {
      const start = pg * PAGE_SIZE;
      const res = await read.pools.getPoolMembersPaginated(pool, start, PAGE_SIZE);
      const ids = res.members.map((n) => n.toString());
      setMembers(ids);
      setTotal(Number(res.total));

      // Batch-fetch user info + eligibility snapshot for each member
      const details = await Promise.all(
        ids.map((id) =>
          Promise.all([
            read.xterra.getUserBasic(id).catch(() => null),
            read.pools.getEligibilitySnapshot(id).catch(() => null),
          ]).then(([u, snap]) => ({ id, u, snap }))
        )
      );
      const map = {};
      details.forEach((d) => { map[d.id] = d; });
      setUsers(map);
    } catch {
      setMembers([]);
      setUsers({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loaded) load(poolTab, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolTab, page, loaded]);

  function switchTab(idx) {
    setPoolTab(idx);
    setPage(0);
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div style={{
      padding: 20,
      background: 'var(--bg-tertiary)',
      border: '1px solid var(--border-soft)',
      borderRadius: 12,
      marginBottom: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <h4 style={{ margin: 0 }}>Eligible Members</h4>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
          Users currently in each pool (auto-added when they qualify)
        </span>
      </div>

      {/* Tab bar with counts */}
      <div style={{
        display: 'flex', gap: 6, padding: 5,
        background: 'var(--bg-primary)',
        borderRadius: 999,
        border: '1px solid var(--border)',
        width: 'fit-content',
        marginBottom: 14,
      }}>
        {POOL_LABELS.map((label, i) => {
          const isActive = poolTab === i;
          return (
            <button
              key={i}
              onClick={() => switchTab(i)}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: 999,
                background: isActive
                  ? `linear-gradient(135deg, ${POOL_COLORS[i]}, ${POOL_COLORS[i]}88)`
                  : 'transparent',
                color: isActive ? 'var(--text-inverse)' : 'var(--text-muted)',
                fontWeight: 700,
                fontSize: 12,
                letterSpacing: '0.05em',
                cursor: 'pointer',
                boxShadow: isActive ? `0 0 12px ${POOL_COLORS[i]}66` : 'none',
              }}
            >
              {label}
              <span style={{
                marginLeft: 8,
                padding: '2px 8px',
                background: isActive ? 'rgba(0,0,0,0.25)' : 'var(--bg-tertiary)',
                borderRadius: 999,
                fontSize: 10,
              }}>
                {counts ? Number(counts[i]) : '—'}
              </span>
            </button>
          );
        })}

        <button
          onClick={() => { setLoaded(true); load(poolTab, page); }}
          disabled={loading}
          style={{
            marginLeft: 8,
            padding: '8px 14px',
            border: '1px solid var(--border)',
            borderRadius: 999,
            background: 'var(--bg-tertiary)',
            color: 'var(--accent)',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {loading ? <Spinner /> : loaded ? '↻ Refresh' : '📥 Load'}
        </button>
      </div>

      {/* Table */}
      {!loaded ? (
        <div style={{
          padding: 30, textAlign: 'center', color: 'var(--text-muted)',
          background: 'var(--bg-primary)', border: '1px dashed var(--border)', borderRadius: 8, fontSize: 13,
        }}>
          Click <strong style={{ color: 'var(--accent)' }}>📥 Load</strong> to fetch {POOL_NAMES[poolTab]} members.
        </div>
      ) : loading && members.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center' }}><Spinner /></div>
      ) : total === 0 ? (
        <div style={{
          padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13,
          background: 'var(--bg-primary)', borderRadius: 8,
        }}>
          No members in {POOL_LABELS[poolTab]} yet.
        </div>
      ) : (
        <>
          <div style={{
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-soft)',
            borderRadius: 8,
            overflowX: 'auto',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 640 }}>
              <thead style={{ background: 'var(--bg-tertiary)' }}>
                <tr style={{ color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 10 }}>
                  <th style={cellHead}>ID</th>
                  <th style={cellHead}>Wallet</th>
                  <th style={{ ...cellHead, textAlign: 'right' }}>Self Invest</th>
                  <th style={{ ...cellHead, textAlign: 'center' }}>Legs Biz</th>
                  <th style={{ ...cellHead, textAlign: 'center' }}>Legs X1</th>
                  <th style={{ ...cellHead, textAlign: 'center' }}>Legs X2</th>
                  <th style={{ ...cellHead, textAlign: 'center' }}>Highest</th>
                  <th style={{ ...cellHead, textAlign: 'center' }}>Override</th>
                </tr>
              </thead>
              <tbody>
                {members.map((id) => {
                  const d = users[id];
                  const snap = d?.snap;
                  const u = d?.u;
                  const highest = snap ? Number(snap.highestQualifyingPool) : 0;
                  return (
                    <tr key={id} style={{ borderTop: '1px solid var(--border-soft)' }}>
                      <td style={{ ...cell, fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontWeight: 700 }}>{id}</td>
                      <td style={{ ...cell, fontFamily: 'var(--font-mono)' }}>{u ? shortenAddress(u.wallet) : '—'}</td>
                      <td style={{ ...cell, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                        {snap ? formatUSDT(snap.selfInvestment, 0) : '—'}
                      </td>
                      <td style={{ ...cell, textAlign: 'center', fontFamily: 'var(--font-mono)' }}>{snap ? Number(snap.legsWithBusinessMet) : '—'}</td>
                      <td style={{ ...cell, textAlign: 'center', fontFamily: 'var(--font-mono)' }}>{snap ? Number(snap.legsWithX1) : '—'}</td>
                      <td style={{ ...cell, textAlign: 'center', fontFamily: 'var(--font-mono)' }}>{snap ? Number(snap.legsWithX2) : '—'}</td>
                      <td style={{ ...cell, textAlign: 'center', fontWeight: 700, color: highest > 0 ? POOL_COLORS[highest - 1] : 'var(--text-muted)' }}>
                        {highest > 0 ? POOL_NAMES[highest - 1] : '—'}
                      </td>
                      <td style={{ ...cell, textAlign: 'center' }}>{snap?.adminOverridden ? '🛡' : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 12 }}>
              <button className="pg-btn" disabled={page === 0} onClick={() => setPage(0)}>«</button>
              <button className="pg-btn" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>‹</button>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                Page {page + 1} / {totalPages}
              </span>
              <button className="pg-btn" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>›</button>
              <button className="pg-btn" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>»</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const cellHead = { padding: '10px 12px', textAlign: 'left', fontWeight: 700 };
const cell     = { padding: '10px 12px', color: 'var(--text-primary)', verticalAlign: 'middle' };
